import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import {
  SaveQueueContext,
  type SaveQueueContextValue,
  type SaveQueueState,
} from './SaveQueueContextTypes'

interface SaveQueueProviderProps {
  children: React.ReactNode
}

export const SaveQueueProvider: React.FC<SaveQueueProviderProps> = ({ children }) => {
  const [saveState, setSaveState] = useState<SaveQueueState>({
    isSaving: false,
    lastSaved: null,
    saveError: null,
    pendingSaveCount: 0,
  })

  // Queue management
  const saveQueueRef = useRef<Array<() => Promise<void>>>([])
  const isProcessingRef = useRef(false)
  const debounceTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const pendingSaveFunctionRef = useRef<(() => Promise<void>) | null>(null)

  // Process the save queue
  const processSaveQueue = useCallback(async () => {
    if (isProcessingRef.current || saveQueueRef.current.length === 0) {
      return
    }

    isProcessingRef.current = true
    setSaveState((prev) => ({ ...prev, isSaving: true }))

    try {
      // Process all queued saves sequentially
      while (saveQueueRef.current.length > 0) {
        const saveFunction = saveQueueRef.current.shift()
        if (saveFunction) {
          await saveFunction()
        }
      }

      setSaveState((prev) => ({
        ...prev,
        isSaving: false,
        lastSaved: new Date(),
        saveError: null,
        pendingSaveCount: 0,
      }))
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error))

      setSaveState((prev) => ({
        ...prev,
        isSaving: false,
        saveError: errorObj,
        pendingSaveCount: saveQueueRef.current.length,
      }))
    } finally {
      isProcessingRef.current = false
      pendingSaveFunctionRef.current = null
    }
  }, [])

  // Enqueue a save with optional debouncing
  const enqueueSave = useCallback(
    (saveFunction: () => Promise<void>, debounceMs?: number) => {
      // Store the save function for potential immediate execution
      pendingSaveFunctionRef.current = saveFunction

      if (debounceMs && debounceMs > 0) {
        // Clear existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
        }

        // Set new debounce timer
        debounceTimerRef.current = setTimeout(() => {
          saveQueueRef.current.push(saveFunction)
          setSaveState((prev) => ({ ...prev, pendingSaveCount: saveQueueRef.current.length }))
          void processSaveQueue()
          debounceTimerRef.current = undefined
        }, debounceMs)
      } else {
        // Immediate save (no debounce)
        saveQueueRef.current.push(saveFunction)
        setSaveState((prev) => ({ ...prev, pendingSaveCount: saveQueueRef.current.length }))
        void processSaveQueue()
      }
    },
    [processSaveQueue]
  )

  // Wait for all pending saves to complete
  const waitForPendingSaves = useCallback(async () => {
    // Cancel any pending debounced save
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = undefined

      // If there was a pending save function, execute it immediately
      if (pendingSaveFunctionRef.current) {
        saveQueueRef.current.push(pendingSaveFunctionRef.current)
        setSaveState((prev) => ({ ...prev, pendingSaveCount: saveQueueRef.current.length }))
      }
    }

    // Wait for the queue to be empty and processing to complete
    while (isProcessingRef.current || saveQueueRef.current.length > 0) {
      await processSaveQueue()
      // Small delay to allow async operations to settle
      await new Promise((resolve) => setTimeout(resolve, 10))
    }
  }, [processSaveQueue])

  // Cancel all pending saves (clears debounce and queue)
  const cancelPendingSaves = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = undefined
    }
    saveQueueRef.current = []
    pendingSaveFunctionRef.current = null
    setSaveState((prev) => ({ ...prev, pendingSaveCount: 0 }))
  }, [])

  // Flush any pending debounced save immediately (non-blocking)
  const flushDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = undefined

      // If there was a pending save function, execute it immediately
      if (pendingSaveFunctionRef.current) {
        saveQueueRef.current.push(pendingSaveFunctionRef.current)
        setSaveState((prev) => ({ ...prev, pendingSaveCount: saveQueueRef.current.length }))
        void processSaveQueue()
      }
    }
  }, [processSaveQueue])

  // Auto-flush on blur: save immediately when leaving text fields
  // This is centralized here so all consumers benefit automatically
  // Uses relatedTarget from FocusEvent for reliable focus detection (no setTimeout race conditions)
  useEffect(() => {
    const isTextField = (el: Element | null): boolean => {
      if (!el || !(el instanceof HTMLElement)) return false
      return el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable
    }

    const handleFocusOut = (e: FocusEvent) => {
      // Only process if we're leaving a text field
      if (!isTextField(e.target as Element)) return

      // relatedTarget is the element receiving focus (null if focus is leaving the document)
      const nextFocusedElement = e.relatedTarget as Element | null
      const movingToTextField = isTextField(nextFocusedElement)

      if (!movingToTextField) {
        // Left text field without moving to another text field - flush pending save
        flushDebounce()
      }
    }

    document.addEventListener('focusout', handleFocusOut, true)

    return () => {
      document.removeEventListener('focusout', handleFocusOut, true)
    }
  }, [flushDebounce])

  const value = useMemo<SaveQueueContextValue>(
    () => ({
      saveState,
      enqueueSave,
      waitForPendingSaves,
      cancelPendingSaves,
      flushDebounce,
    }),
    [saveState, enqueueSave, waitForPendingSaves, cancelPendingSaves, flushDebounce]
  )

  return <SaveQueueContext.Provider value={value}>{children}</SaveQueueContext.Provider>
}
